import React, { useEffect, useMemo } from 'react'
import { Box, Stack } from '@mui/material'
import { useSelector } from 'react-redux'
import { debounce } from 'lodash'

import { TypesenseProduct } from '../../typesense/types'
import { SearchResponse } from 'typesense/lib/Typesense/Documents'
import ContactCard from '../../../components/ContactCard'
import Pagination, { PaginationCounter, PaginationDetails } from '../../../components/Pagination'
import { PAGINATION_DATA_LIMIT, SortingOptions } from '../../../util/consts'
import ProductList from '../../products/components/ProductList'
import ProductListContainer from '../../products/pages/ProductsListContainer'
import { RelationshipOwner, selectHomeBranch } from '../../azure-b2c/storage'
import { ActivePrice, Product } from '../../products/storage'
import TestIds from '../../../testIds'
import { SelectedFiltersType } from '../../products/components/ProductFilter'
import { multiSearchWithActivePrices } from '../../typesense/api'
import { Loader, LoadingBox } from '../../../components/Loader'
import { generateImageUrl } from '../../../util/helpers'
import { sortOptions } from '../../typesense/Search'
import { SeedSearchProductCard } from './SeedSearchProductCard'
import { SeedTypesenseProduct } from '../types/SeedOrderFormType'
import { useOnlineStatus } from '../../../util/useOnlineStatus'
import MaterialSymbol from '../../../components/MaterialSymbol'
import { locationToSiteId } from '../../stores/storage/utils'

interface SeedSearchProps extends Record<string, unknown> {
  accountNumber: string | undefined
  data: Product[]
  handleClearFilters: () => void
  isFilterApplied: boolean
  sortBy: string
  setSortBy: React.Dispatch<React.SetStateAction<string>>
  selectedFilters: SelectedFiltersType
  currentPage: number
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>
  relationshipOwner: RelationshipOwner
  seedId?: string
}

export const SeedSearch: React.FC<SeedSearchProps> = ({
  accountNumber,
  data,
  handleClearFilters,
  isFilterApplied,
  sortBy,
  selectedFilters,
  currentPage,
  relationshipOwner,
  setSortBy,
  setCurrentPage,
}) => {
  const [hits, setHits] = React.useState<SearchResponse<TypesenseProduct & ActivePrice>>()
  const [loading, setLoading] = React.useState<boolean>(true)
  const [error, setError] = React.useState(false)
  const onlineStatus = useOnlineStatus()
  const homeBranchId = useSelector(selectHomeBranch)

  const paginationCounter = useMemo(() => {
    const count = hits?.found ?? 0
    const total = hits?.found
    const currentPage = hits?.page ?? 0

    return (
      <PaginationCounter
        data-testid={TestIds.components.pagination.number}
        label='products'
        itemsPerPage={PAGINATION_DATA_LIMIT}
        count={count}
        total={total}
        currentPage={currentPage}
      />
    )
  }, [hits, currentPage, selectedFilters, isFilterApplied])

  const pagination = useMemo(() => {
    const total = hits?.found
    const _currentPage = hits?.page ?? currentPage

    if (hits && total && total > PAGINATION_DATA_LIMIT) {
      return (
        <Pagination
          path='/seed'
          page={_currentPage}
          onClickPageNumber={(page) => setCurrentPage(page)}
          count={Math.ceil(total / PAGINATION_DATA_LIMIT)}
        />
      )
    }
  }, [hits])

  useEffect(() => {
    ;(async () => {
      if (!sortOptions.includes(sortBy as SortingOptions)) {
        setSortBy(sortOptions[0])
      }
    })()
  }, [])

  const fetchResults = async (
    selectedFilters: SelectedFiltersType,
    sortBy: string,
    currentPage: number,
  ) => {
    try {
      setError(false)
      setLoading(true)

      const branchId = locationToSiteId(homeBranchId ?? '')
      // multiSearchWithActivePrice if you want to wait for results in one go
      const { searchResults } = await multiSearchWithActivePrices({
        accountNumber,
        currentPage,
        selectedFilters,
        sortBy,
        branchId,
      })
      setHits(searchResults)
      setLoading(false)
    } catch (error) {
      setError(true)
      console.error('Error fetching results:', error)
    }
  }

  // fetchResults(selectedFilters, sortBy, currentPage)

  useEffect(() => {
    setCurrentPage(0)
  }, [
    selectedFilters.category.value,
    selectedFilters.searchText.value,
    selectedFilters.seedType?.value,
  ])
  const debouncedFetch = debounce(
    (filters, sort, page) => fetchResults(filters, sort, page),
    1000,
    {
      trailing: true,
    },
  )

  useEffect(() => {
    if (onlineStatus || hits === undefined) {
      debouncedFetch(selectedFilters, sortBy, currentPage)
    }
  }, [
    onlineStatus,
    selectedFilters.category.value,
    selectedFilters.searchText.value,
    selectedFilters.seedType?.value,
    selectedFilters.storeId.value,
    sortBy,
    currentPage,
  ])

  const productsToRender = React.useMemo(() => {
    if (hits) {
      const hitsToRender = hits
      return hitsToRender?.hits?.map((hit) => hit.document)
    }
  }, [hits, selectedFilters])

  if (!hits && !loading) {
    return null
  }

  if (error || !onlineStatus) {
    return (
      <Stack>
        <Box display='flex' margin='auto' padding='2' alignContent={'center'} gap={2}>
          <MaterialSymbol>wifi_off</MaterialSymbol>
          <span>You appear to be offline, reconnect and we will try again</span>
        </Box>
      </Stack>
    )
  }
  if (loading)
    return (
      <LoadingBox>
        <Loader
          title='Loading your products'
          type='inline-flex'
          sx={{ marginTop: '12px' }}
          data-testid={TestIds.components.loader.searchProduct}
        />
      </LoadingBox>
    )
  return (
    <ProductListContainer>
      <PaginationDetails
        onClear={handleClearFilters}
        showClear={isFilterApplied}
        paginationCounter={paginationCounter}
        sorting={{
          value: sortBy,
          onChange: setSortBy,
          options: sortOptions,
        }}
      />
      {productsToRender && (
        <ProductList<SeedTypesenseProduct & ActivePrice>
          items={productsToRender}
          renderListItem={(e) => {
            const hit = e
            const match = data.find((e) => e.productId === hit.id, 10)

            return (
              <Box key={e.productId}>
                <SeedSearchProductCard
                  key={hit.id}
                  item={{
                    category: hit.l1_category,
                    subCategory: hit.l2_category,
                    name: hit.name,
                    description: hit.name,
                    lifeCycleState: hit.lifecycle,
                    masterProductId: hit.id,
                    productId: hit.productId,
                    orders: [...(match?.orders ?? [])],
                    itemNumber: hit.item_id,
                    imageUrl: generateImageUrl(hit.item_id),
                    basePriceExcGst: hit.basePriceExcGst,
                    basePriceIncGst: hit.price,
                    discountedPriceExcGst: hit.discountedPriceExcGst,
                    discountedPriceIncGst: hit.discountedPriceIncGst,
                    hasVariants: hit.has_variants,
                    isPriceNegotiable: hit.price_negotiable,
                    stockAvailabilities: hit.stockAvailabilities,
                    stockAvailability: hit.stock,
                  }}
                />
              </Box>
            )
          }}
        />
      )}
      {pagination}
      {hits && hits.found < 25 && (
        <ContactCard
          title='Looking to buy larger volumes, please reach out to us below for assistance.'
          sx={{ minWidth: '100%' }}
          {...relationshipOwner}
        />
      )}
    </ProductListContainer>
  )
}
